import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomNotification, dialogFunction } from 'common/components';

import {
  getMemberBillingStatements,
  saveMemberBillingProperties,
  addCharge,
  downloadBillingStatement,
  editMemberPaymentSetting,
  getAuthorizedCardUsers,
  saveAuthorizedCardUser,
  removeAuthorizedCardUsers,
  exportBillingStatement,
  downloadBillingReceipt,
  addBillingNote,
  getBillingNote,
  getMemberDiscountProperties,
  saveMemberDiscountProperties,
  getMemberBillingSetupProperties,
  saveMemberBillingSetupProperties,
  recalculateMembershipFee,
  getPricingMatrix,
} from 'services/payment';

import { useCheckCreditCardPermission } from 'hooks/useCheckCreditCardPermission';

const keys = {
  //* api/payment/get-member-payment-setting
  allMemberBillingStatement: () => ['payment', 'get-member-billing-statements'],
  memberBillingStatements: (memberId, billingPeriod) => [
    'payment',
    'get-member-billing-statements',
    parseInt(memberId),
    billingPeriod,
  ],
  downloadBillingStatements: ['payment', 'download-member-billing-charges'],
  getSavedMemberCard: (memberId) => [
    'payment',
    'get-member-saved-cards',
    { params: { memberId: parseInt(memberId), paymentEndpointType: null } },
  ],
  //* /api/payment/get-member-payment-setting
  getMemberPaymentSetting: (memberId) => {
    const baseKey = ['payment', 'get-member-payment-setting'];

    if (!memberId) {
      return baseKey;
    }

    return [...baseKey, { params: { memberId: parseInt(memberId) } }];
  },
  getAuthorizedCardUsers: ({ memberId, memberPaymentProfileId }) => [
    'payment',
    'get-authorized-card-users',
    parseInt(memberId),
    parseInt(memberPaymentProfileId),
  ],
  downloadBillingReceipt: ['payment', 'download-invoice-summary'],
  getBillingNote: (memberId) => {
    const baseKey = ['payment', 'get-billing-note-grid'];

    if (!memberId) {
      return baseKey;
    }

    return [...baseKey, { params: { memberId: parseInt(memberId) } }];
  },
  getMemberDiscountProperties: (memberId) => [
    'payment',
    'get-member-discount-properties',
    parseInt(memberId),
  ],
  getMemberBillingSetupProperties: (memberId) => [
    'payment',
    'get-member-billing-setup-properties',
    parseInt(memberId),
  ],
  getPricingMatrix: (memberId) => [
    'payment',
    'pricing-matrix',
    parseInt(memberId),
  ],
};

/**
 *? POST
 *? /api/payment/get-member-billing-statements
 */
const useQueryMemberBillingStatements = (
  { memberId, billingPeriod },
  options = {}
) => {
  const queryClient = useQueryClient();

  const params = {
    memberId,
    ...billingPeriod,
    pageIndex: 1,
    pageSize: 999999999,
  };

  const key = keys.memberBillingStatements(memberId, billingPeriod);

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await getMemberBillingStatements(params);

      const { isSuccess, data, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(data);
      }
      const err = message || 'Server Error';
      CustomNotification.error(err);
      return Promise.reject(err);
    },
    ...options,
  });

  const invalidateMemberBillingStatement = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return { ...query, invalidateMemberBillingStatement };
};

/**
 *? POST
 *? /api/payment/save-member-billing-properties
 */
const useMutateBillingProperty = (options) => {
  return useMutation({
    mutationFn: async (params) => {
      const res = await saveMemberBillingProperties(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        CustomNotification.success('Change payment term successfully!');
        return Promise.resolve(true);
      } else {
        const errorMsg = message || 'Server Error';
        CustomNotification.error(errorMsg);
      }
    },
    ...options,
  });
};

/**
 *? POST
 *? /api/payment/add-billing-charge
 */
const useMutateAddBillingCharge = ({ onCancel, ...options } = {}) => {
  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();

  return useMutation({
    mutationFn: async (params) => {
      const res = await addCharge(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        CustomNotification.success('Add Charge Successfully!');
      } else {
        const errorMsg = message || 'Server Error';
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
          onOk: () => {
            onCancel?.(); //* close add Change modal
          },
        });
      }
      refetchAllMemberBillingStatement();

      return res;
    },
    ...options,
  });
};

/**
 *? POST
 *? /api/payment/add-billing-charge
 */
const useQueryDownloadBillingStatement = ({ memberId, chargeIds }) => {
  return useQuery({
    queryKey: keys.downloadBillingStatements,
    queryFn: async () => {
      const params = { memberId, idsCharges: chargeIds };
      const res = await downloadBillingStatement(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(true);
      } else {
        const errorMsg = message || 'Server Error';
        CustomNotification.error(errorMsg);
      }
    },
    enabled: false,
    retry: false,
  });
};
/**
 *? POST
 *? /api/payment/download-invoice-summary
 */
const useExportBillingStatement = ({ memberId, chargeIds }) => {
  return useQuery({
    queryKey: keys.downloadBillingStatements,
    queryFn: async () => {
      const params = { memberId, billingStatementIds: chargeIds };
      const res = await exportBillingStatement(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(true);
      } else {
        const errorMsg = message || 'Server Error';
        CustomNotification.error(errorMsg);
      }
    },
    enabled: false,
    retry: false,
  });
};
const useQuerySavedMemberCard = ({ memberId, ...options }) => {
  const { canViewSavedCard } = useCheckCreditCardPermission();
  const queryClient = useQueryClient();

  const queryObject = useQuery({
    queryKey: keys.getSavedMemberCard(memberId),
    enabled: Boolean(memberId && canViewSavedCard),
    ...options,
  });

  const invalidateSavedMemberCard = (_memberId) => {
    queryClient.invalidateQueries({
      queryKey: keys.getSavedMemberCard(_memberId || memberId),
    });
  };

  const fetchSavedMemberCard = async () => {
    await queryClient.resetQueries({
      queryKey: keys.getSavedMemberCard(memberId),
      exact: true,
    });

    return queryClient.fetchQuery({
      queryKey: keys.getSavedMemberCard(memberId),
    });
  };

  return { ...queryObject, invalidateSavedMemberCard, fetchSavedMemberCard };
};

/**
 *? GET
 *? /api/payment/get-member-payment-setting
 */
const useQueryMemberPaymentSetting = ({ memberId }) => {
  const queryClient = useQueryClient();
  const key = keys.getMemberPaymentSetting(memberId);

  const { canAccessBilling } = useCheckCreditCardPermission();

  const queryObject = useQuery({
    queryKey: key,
    enabled: Boolean(memberId) && canAccessBilling,
     meta: {
      onError: () => false,
    },
  });

  const invalidateMemberPaymentSetting = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return {
    ...queryObject,
    invalidateMemberPaymentSetting,
  };
};

/**
 *? POST
 *? /api/payment/edit/edit-payment-setting
 */
const useMutateMemberPaymentSetting = () => {
  return useMutation({
    mutationFn: async (params) => {
      const res = await editMemberPaymentSetting(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        CustomNotification.success(
          'Member fees have been updated successfully'
        );
        return Promise.resolve(true);
      } else {
        const errMsg = message || 'Failed to update member fees';
        CustomNotification.error(errMsg);
        return Promise.reject(errMsg);
      }
    },
  });
};

const useQueryGetAuthorizedCardUsers = ({
  memberId,
  memberPaymentProfileId,
}) => {
  return useQuery({
    queryKey: keys.getAuthorizedCardUsers({
      memberId,
      memberPaymentProfileId,
    }),
    queryFn: async () => {
      const params = { memberId, memberPaymentProfileId };
      const res = await getAuthorizedCardUsers(params);
      const { isSuccess, message, data } = res || {};

      if (isSuccess) {
        return data?.authorizedUsers || [];
      } else {
        const errorMsg = message || 'Server Error';
        CustomNotification.error(errorMsg);
      }
    },
  });
};

const useMutateSaveAuthorizedCardUser = () => {
  return useMutation({
    mutationFn: saveAuthorizedCardUser,
  });
};

const useMutateRemoveAuthorizedCardUser = () => {
  return useMutation({
    mutationFn: removeAuthorizedCardUsers,
  });
};

const useRefetchAllMemberBillingStatement = () => {
  const queryClient = useQueryClient();

  const refetchAllMemberBillingStatement = () => {
    queryClient.invalidateQueries({
      queryKey: keys.allMemberBillingStatement(),
    });
  };
  return refetchAllMemberBillingStatement;
};

const useQueryDownloadBillingReceipt = ({ billingStatementIds }) => {
  return useQuery({
    queryKey: keys.downloadBillingReceipt,
    queryFn: async () => {
      try {
        const params = { billingStatementIds };
        const res = await downloadBillingReceipt(params);
        const { isSuccess, message } = res || {};

        if (isSuccess) {
          return Promise.resolve(true);
        } else {
          const errorMsg = message || 'Server Error';
          CustomNotification.error(errorMsg);
        }
      } catch (error) {
        CustomNotification.error('Server Error');
      }
    },
    enabled: false,
    retry: false,
  });
};

const useQueryBillingNote = ({ memberId, ...restProps }) => {
  const queryClient = useQueryClient();

  const params = {
    memberId,
    pageIndex: 1,
    pageSize: 999999999,
  };

  const key = keys.getBillingNote(memberId);

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await getBillingNote(params);

      const { isSuccess, data, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(data?.gridData);
      }
      const err = message || 'Something went wrong!';
      CustomNotification.error(err);
      return Promise.reject(err);
    },
    ...restProps,
  });

  const invalidateBillingNote = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return { ...query, invalidateBillingNote };
};

const useMutateAddBillingNote = () => {
  return useMutation({
    mutationFn: addBillingNote,
  });
};

const useQueryMemberDiscountProperties = ({ memberId, ...restProps }) => {
  const queryClient = useQueryClient();
  const key = keys.getMemberDiscountProperties(memberId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await getMemberDiscountProperties({ memberId });

      const { isSuccess, data, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(data);
      }
      const err = message || 'Something went wrong!';
      CustomNotification.error(err);
      return Promise.reject(err);
    },
    ...restProps,
  });

  const invalidateMemberDiscountProperties = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return {
    ...query,
    invalidateMemberDiscountProperties,
  };
};

const useMutateSaveMemberDiscountProperties = () => {
  return useMutation({
    mutationFn: saveMemberDiscountProperties,
  });
};

const useQueryMemberBillingSetupProperties = ({ memberId, ...restProps }) => {
  const queryClient = useQueryClient();
  const key = keys.getMemberBillingSetupProperties(memberId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await getMemberBillingSetupProperties({ memberId });

      const { isSuccess, data, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(data);
      }
      const err = message || 'Something went wrong!';
      CustomNotification.error(err);
      return Promise.reject(err);
    },
    ...restProps,
  });

  const invalidateMemberBillingSetupProperties = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return {
    ...query,
    invalidateMemberBillingSetupProperties,
  };
};

const useMutateSaveMemberBillingSetupProperties = () => {
  return useMutation({
    mutationFn: async (params) => {
      const res = await saveMemberBillingSetupProperties(params);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        CustomNotification.success('Billing setup updated successfully');
        return Promise.resolve(true);
      } else {
        const errMsg = message || 'Failed to update billing setup';
        CustomNotification.error(errMsg);
        return Promise.reject(errMsg);
      }
    },
    onError: (error) => {
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to update billing setup';
      CustomNotification.error(errMsg);
    },
  });
};

/**
 *? POST
 *? /api/payment/recalculate-membership-fee/{memberId}
 */
const useMutateRecalculateMembershipFee = () => {
  return useMutation({
    mutationFn: async (memberId) => {
      const res = await recalculateMembershipFee(memberId);
      const { isSuccess, message } = res || {};

      if (isSuccess) {
        CustomNotification.success(
          'Membership fee has been recalculated successfully'
        );
        return Promise.resolve(true);
      }
      const errMsg = message || 'Failed to recalculate membership fee';
      CustomNotification.error(errMsg);
      return Promise.reject(errMsg);
    },
  });
};

/**
 *? GET
 *? /api/payment/pricing-matrix/{memberId}
 */
const useQueryPricingMatrix = ({ memberId, ...restProps }) => {
  const queryClient = useQueryClient();
  const key = keys.getPricingMatrix(memberId);

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await getPricingMatrix(memberId);

      const { isSuccess, data, message } = res || {};

      if (isSuccess) {
        return Promise.resolve(data);
      }
      const err = message || 'Something went wrong!';
      CustomNotification.error(err);
      return Promise.reject(err);
    },
    ...restProps,
  });

  const invalidatePricingMatrix = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  return { ...query, invalidatePricingMatrix };
};

export {
  keys,
  useQueryMemberBillingStatements,
  useMutateBillingProperty,
  useMutateAddBillingCharge,
  useQueryDownloadBillingStatement,
  useQuerySavedMemberCard,
  useQueryMemberPaymentSetting,
  useMutateMemberPaymentSetting,
  useQueryGetAuthorizedCardUsers,
  useMutateSaveAuthorizedCardUser,
  useMutateRemoveAuthorizedCardUser,
  useRefetchAllMemberBillingStatement,
  useExportBillingStatement,
  useQueryDownloadBillingReceipt,
  useQueryBillingNote,
  useMutateAddBillingNote,
  useQueryMemberDiscountProperties,
  useMutateSaveMemberDiscountProperties,
  useQueryMemberBillingSetupProperties,
  useMutateSaveMemberBillingSetupProperties,
  useMutateRecalculateMembershipFee,
  useQueryPricingMatrix,
};
